// BitrixVue Options API — типизация this в methods избыточна для runtime-компонента.
// @ts-nocheck

import { BitrixVue } from 'ui.vue3';

import {
	createEmptyPlace,
	formatNumber,
	parseVal,
	sanitizeNumeric,
	validateMaxDimensions,
} from './helpers';
import { calculatorTemplate } from './template';
import type {
	CalculateRequest,
	CalculatorConfig,
	CalculatorMode,
	CityDirection,
	PackagePreview,
	PlaceData,
	PlaceTab,
} from './types';

type SearchTimers = Partial<Record<CityDirection, ReturnType<typeof setTimeout>>>;

export function createCalculatorComponent(config: CalculatorConfig, searchTimers: SearchTimers): object {
	const initial = config.initial ?? {};

	return {
		data(): {
			messages: Record<string, string>;
			mode: CalculatorMode;
			cityFrom: string;
			cityTo: string;
			fromCheck: boolean;
			toCheck: boolean;
			places: PlaceData[];
			activePlaceIndex: number;
			commonModeData: PlaceData;
			dimsError: boolean;
			validationError: boolean;
			loading: boolean;
			calculated: boolean;
			orderHtml: string;
			cityFromSuggestions: string[];
			cityToSuggestions: string[];
			cityFromOpen: boolean;
			cityToOpen: boolean;
			cityFromLoading: boolean;
			cityToLoading: boolean;
		} {
			return {
				messages: config.messages ?? {},
				mode: 'common',
				cityFrom: initial.cityFrom ?? '',
				cityTo: initial.cityTo ?? '',
				fromCheck: false,
				toCheck: false,
				places: [createEmptyPlace()],
				activePlaceIndex: 0,
				commonModeData: Object.assign(createEmptyPlace(), {
					weight: initial.weight ?? '',
					volume: initial.volume ?? '',
					quantity: initial.place ?? '',
					declaredValue: initial.cargoValue ?? '',
				}),
				dimsError: false,
				validationError: false,
				loading: false,
				calculated: false,
				orderHtml: '',
				cityFromSuggestions: [],
				cityToSuggestions: [],
				cityFromOpen: false,
				cityToOpen: false,
				cityFromLoading: false,
				cityToLoading: false,
			};
		},
		computed: {
			currentData(this: { mode: CalculatorMode; commonModeData: PlaceData; places: PlaceData[]; activePlaceIndex: number }): PlaceData | undefined {
				return this.mode === 'common' ? this.commonModeData : this.places[this.activePlaceIndex];
			},
			volumeDisabled(this: { mode: CalculatorMode }): boolean {
				return this.mode === 'place';
			},
			packagePreview(this: {
				mode: CalculatorMode;
				places: PlaceData[];
				calcPackagePreview: (data?: PlaceData) => PackagePreview;
				currentData?: PlaceData;
			}): PackagePreview {
				return this.calcPackagePreview(this.currentData);
			},
			placeTabs(this: { mode: CalculatorMode; places: PlaceData[] }): PlaceTab[] {
				if (this.mode !== 'place') {
					return [];
				}

				const tabs: PlaceTab[] = [];
				let boxNumber = 1;

				for (let i = 0; i < this.places.length; i++) {
					const qty = parseVal(this.places[i].quantity) || 1;
					const label = qty > 1
						? `${boxNumber}-${boxNumber + qty - 1} места`
						: `${boxNumber} место`;

					tabs.push({ index: i, label, qty });
					boxNumber += qty;
				}

				return tabs;
			},
		},
		methods: {
			msg(code: string): string {
				return this.messages[code] ?? code;
			},
			swapCities(): void {
				const tmp = this.cityFrom;
				this.cityFrom = this.cityTo;
				this.cityTo = tmp;
			},
			switchMode(mode: CalculatorMode): void {
				this.mode = mode;
				if (mode === 'place' && this.places.length === 0) {
					this.places = [createEmptyPlace()];
				}
				this.recalcDerived();
			},
			switchTab(index: number): void {
				this.activePlaceIndex = index;
				this.recalcDerived();
			},
			addPlace(): void {
				this.places.push(createEmptyPlace());
				this.activePlaceIndex = this.places.length - 1;
			},
			deletePlace(index: number): void {
				if (this.places.length <= 1) {
					return;
				}

				this.places.splice(index, 1);
				this.activePlaceIndex = Math.max(0, Math.min(this.activePlaceIndex, this.places.length - 1));
				this.recalcDerived();
			},
			updateField(field: keyof PlaceData, value: string): void {
				const data = this.currentData;
				if (!data) {
					return;
				}

				if (['weight', 'long', 'width', 'height', 'declaredValue', 'quantity', 'volume'].includes(field)) {
					value = sanitizeNumeric(value);
				}

				(data as Record<string, string | boolean>)[field] = value;

				if (['long', 'width', 'height'].includes(field)) {
					this.recalcVolume();
				}
			},
			onCheckboxChange(field: keyof PlaceData, event: Event): void {
				const target = event.target as HTMLInputElement | null;
				if (!this.currentData || !target) {
					return;
				}

				(this.currentData as Record<string, string | boolean>)[field] = target.checked;
				this.recalcDerived();
			},
			onQuantityBlur(): void {
				const data = this.currentData;
				if (!data) {
					return;
				}

				const val = parseInt(data.quantity, 10);
				data.quantity = (Number.isNaN(val) || val < 1) ? '' : String(val);
				this.recalcDerived();
			},
			recalcVolume(): void {
				const data = this.currentData;
				if (!data) {
					return;
				}

				const w = parseVal(data.width);
				const h = parseVal(data.height);
				const l = parseVal(data.long);
				this.dimsError = !validateMaxDimensions(l, w, h);

				if (this.mode === 'place') {
					const rawCalc = w * h * l;
					data.volume = (w >= 0.001 && h >= 0.001 && l >= 0.001)
						? rawCalc.toFixed(3)
						: '0.001';
				}
			},
			recalcDerived(): void {
				this.recalcVolume();
			},
			calcPackagePreview(data?: PlaceData): PackagePreview {
				if (!data) {
					return { crate: '', pallet: '', stretchFilm: '' };
				}

				const volume = parseVal(data.volume);
				const qty = parseVal(data.quantity) || 1;
				let l = parseVal(data.long);
				let w = parseVal(data.width);
				let h = parseVal(data.height);
				const calcPlaces = this.mode === 'common' ? qty : this.places.length;

				const obBasePrice = { crate: 900, pallet: 350, stretchFilm: 250 };
				const obPriceByVolume = { crate: 1850, pallet: 700, stretchFilm: 250 };
				let markup = 1;

				if (this.mode === 'place') {
					if (calcPlaces > 1) {
						l /= calcPlaces;
						w /= calcPlaces;
						h /= calcPlaces;
					}

					if (l >= 4 || w >= 4 || h >= 4) markup = 2;
					else if (l >= 3 || w >= 3 || h >= 3) markup = 1.5;
					else if (l >= 2 || w >= 2 || h >= 2) markup = 1.25;
				}

				obBasePrice.crate *= markup;
				obPriceByVolume.crate *= markup;

				const multiplier = this.mode === 'common' ? 1 : qty;

				if (volume < 0.5 || Number.isNaN(volume)) {
					return {
						crate: ` + ${obBasePrice.crate * qty} руб.`,
						pallet: ` + ${obBasePrice.pallet * qty} руб.`,
						stretchFilm: ` + ${obBasePrice.stretchFilm * qty} руб.`,
					};
				}

				return {
					crate: ` + ${formatNumber(volume * obPriceByVolume.crate * multiplier)} руб.`,
					pallet: ` + ${formatNumber(Math.ceil(volume) * obPriceByVolume.pallet * multiplier)} руб.`,
					stretchFilm: ` + ${formatNumber(Math.ceil(volume) * obPriceByVolume.stretchFilm * multiplier)} руб.`,
				};
			},
			debouncedCitySearch(direction: CityDirection, term: string): void {
				clearTimeout(searchTimers[direction]);
				searchTimers[direction] = setTimeout(() => {
					this.searchCity(direction, term);
				}, 300);
			},
			searchCity(direction: CityDirection, term: string): void {
				const normalizedTerm = String(term || '').trim();
				const action = direction === 'from' ? 'searchCityFrom' : 'searchCityTo';
				const data: Record<string, string> = { term: normalizedTerm };

				if (direction === 'to') {
					data.cityFrom = this.cityFrom;
				}

				if (normalizedTerm.length < 1 || (direction === 'to' && !this.cityFrom.trim())) {
					if (direction === 'from') {
						this.cityFromSuggestions = [];
					} else {
						this.cityToSuggestions = [];
					}

					return;
				}

				if (direction === 'from') {
					this.cityFromLoading = true;
				} else {
					this.cityToLoading = true;
				}

				BX.ajax.runComponentAction('fte:calculator', action, {
					mode: 'class',
					data,
				}).then((response: { data?: { items?: string[] } }) => {
					const items = response.data?.items ?? [];

					if (direction === 'from') {
						this.cityFromSuggestions = items;
						this.cityFromOpen = items.length > 0;
					} else {
						this.cityToSuggestions = items;
						this.cityToOpen = items.length > 0;
					}
				}).catch(() => {
					if (direction === 'from') {
						this.cityFromSuggestions = [];
					} else {
						this.cityToSuggestions = [];
					}
				}).finally(() => {
					if (direction === 'from') {
						this.cityFromLoading = false;
					} else {
						this.cityToLoading = false;
					}
				});
			},
			selectCity(direction: CityDirection, value: string): void {
				if (direction === 'from') {
					this.cityFrom = value;
					this.cityFromOpen = false;
					this.cityFromSuggestions = [];
				} else {
					this.cityTo = value;
					this.cityToOpen = false;
					this.cityToSuggestions = [];
				}
			},
			validateForm(): CalculateRequest | null {
				let hasError = false;
				this.validationError = false;

				if (!this.cityFrom.trim() || !this.cityTo.trim()) {
					hasError = true;
				}

				if (this.dimsError) {
					hasError = true;
				}

				let itemsToSend: CalculateRequest['items'] = [];
				let totalWeight = 0;
				let totalVolume = 0;
				let totalPlacesCount = 0;

				if (this.mode === 'common') {
					const data = this.commonModeData;
					const w = parseVal(data.weight);
					const v = parseVal(data.volume);
					const l = parseVal(data.long);
					const width = parseVal(data.width);
					const h = parseVal(data.height);
					const qty = parseVal(data.quantity) || 1;

					if (!w && !v) hasError = true;
					if (data.crate && (!l || !width || !h)) hasError = true;

					totalWeight = w;
					totalVolume = v;
					totalPlacesCount = qty;

					itemsToSend.push({
						weight: w,
						volume: v,
						long: l,
						width,
						height: h,
						declaredValue: parseVal(data.declaredValue),
						quantity: qty,
						crate: data.crate,
						pallet: data.pallet,
						stretchFilm: data.stretchFilm,
					});
				} else {
					for (const place of this.places) {
						const placeQty = parseVal(place.quantity) || 1;
						const placeW = parseVal(place.weight);
						const placeV = parseVal(place.volume);
						const placeL = parseVal(place.long);
						const placeWidth = parseVal(place.width);
						const placeH = parseVal(place.height);

						totalWeight += placeW * placeQty;
						totalVolume += placeV * placeQty;
						totalPlacesCount += placeQty;

						if (place.crate && (!placeL || !placeWidth || !placeH)) hasError = true;
						if (!placeW && !placeV) hasError = true;
						if (!validateMaxDimensions(placeL, placeWidth, placeH)) hasError = true;
					}

					itemsToSend = this.places;
				}

				if (hasError) {
					this.validationError = true;
					return null;
				}

				return {
					type: this.mode,
					city_from: this.cityFrom,
					city_to: this.cityTo,
					place: String(totalPlacesCount),
					weight: totalWeight.toFixed(2),
					volume: totalVolume.toFixed(3),
					from_check: this.fromCheck ? 'true' : '',
					to_check: this.toCheck ? 'true' : '',
					items: itemsToSend,
				};
			},
			submitCalculate(): void {
				const requestData = this.validateForm();
				if (!requestData) {
					return;
				}

				this.loading = true;
				this.validationError = false;

				BX.ajax.runComponentAction('fte:calculator', 'calculate', {
					mode: 'class',
					json: requestData,
				}).then((response: { data?: { result?: { html?: string }; debug?: unknown } }) => {
					if (response.data?.result?.html) {
						this.orderHtml = response.data.result.html;
						this.calculated = true;

						if (response.data.debug) {
							console.log(response.data.debug);
						}
					}
				}).catch((error: unknown) => {
					console.error('Ошибка расчёта:', error);
				}).finally(() => {
					this.loading = false;
				});
			},
		},
		mounted(): void {
			this.recalcDerived();
		},
		template: calculatorTemplate,
	};
}

export function createCalculatorApp(config: CalculatorConfig): ReturnType<typeof BitrixVue.createApp> {
	const searchTimers: SearchTimers = {};
	return BitrixVue.createApp(createCalculatorComponent(config, searchTimers));
}
